import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  getEvent, getEventOpportunity,
  type EventDetail, type EventOpportunity,
} from '../../lib/catalog';
import { ApiError } from '../../lib/apiClient';
import { resolveEventPage, type CmsResolution } from '../../lib/cms';
import { CopyProvider } from '../../copy/CopyProvider';
import { CmsContextProvider } from '../../cms/context';
import { CmsThemeProvider } from '../../cms/ThemeProvider';
import { GlobalSectionProvider } from '../../cms/globalSections';
import {
  DocumentRenderer, documentUsesCriticalBlock, type RenderProblem,
} from '../../cms/BlockRenderer';
// The single source of which blocks are critical. Duplicating the list here is how the
// two would eventually disagree about whether losing the ticket panel matters.
import { isCriticalComponent } from '../../cms/registry';
import { EmergencyEventPage } from '../../cms/EmergencyEventPage';
import { reportCms } from '../../cms/diagnostics';
import './eventDetail.css';

/**
 * The public Event page.
 *
 * ── THE URL DOES NOT CHANGE, AND NEITHER DOES THE PAGE ───────────────────────────────
 *
 * /events/:eventId renders here. The host loads the event exactly as the page always did,
 * asks the engine what to render, and then takes one of two paths that produce the same
 * output: a CMS document composed of blocks, or the hardcoded page. The blocks and the
 * hardcoded page are built from THE SAME components, so "identical" is a property of the
 * code rather than a claim about it.
 *
 * ── ONE COMPOSITION, PLUS A NARROW EMERGENCY ONE ─────────────────────────────────────
 *
 * The CMS document is authoritative for a normal render. When there is no usable
 * document, EmergencyEventPage composes THE SAME BLOCKS in code — it is a handful of
 * component calls, not a second implementation of the page. Task 10 fell back to a
 * complete duplicate of Event Detail; keeping that would have meant every future change
 * being made twice, with the forgotten copy being the one that runs when something has
 * already gone wrong.
 *
 * The emergency path is taken when:
 *
 *   the resolve call fails, times out, or 500s
 *   no template is configured, or the platform default has been archived
 *   the document is for a schema version this build does not know
 *   a block in it is unknown to this build's registry
 *   a block is used in the wrong context, or pinned to a version this build has moved past
 *   the CRITICAL ticket block throws while rendering
 *
 * A NON-CRITICAL block that throws is dropped by its own boundary and the page carries
 * on, because a broken lineup should not cost somebody a ticket. The ticket panel is
 * critical: a page that renders without it looks fine and sells nothing.
 */
export function EventPageHost() {
  const { eventId } = useParams<{ eventId: string }>();
  return (
    <CopyProvider scope={{ eventId }}>
      <EventPageBody />
    </CopyProvider>
  );
}

function EventPageBody() {
  const { eventId } = useParams<{ eventId: string }>();

  const [event, setEvent] = useState<EventDetail | null>(null);
  const [opportunity, setOpportunity] = useState<EventOpportunity | null>(null);
  const [cms, setCms] = useState<CmsResolution | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [cmsSettled, setCmsSettled] = useState(false);

  // Set when the CMS path proves unusable. Never unset for the life of the page: having
  // fallen back, flipping to a half-broken CMS render on a later state change would be
  // worse than staying on the page that works.
  const [cmsUnusable, setCmsUnusable] = useState(false);
  const problems = useRef<RenderProblem[]>([]);

  useEffect(() => {
    if (!eventId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCmsSettled(false);
    setCmsUnusable(false);
    problems.current = [];

    getEvent(eventId)
      .then((result) => { if (!cancelled) setEvent(result); })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError && err.status === 404
          ? 'This event is not available.'
          : 'Could not load this event.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    // Supplementary — a failure here must not break the page.
    getEventOpportunity(eventId)
      .then((result) => { if (!cancelled) setOpportunity(result); })
      .catch(() => { if (!cancelled) setOpportunity(null); });

    // In parallel with the event, not after it: the CMS lookup must not add a round trip
    // to the time before anything appears.
    resolveEventPage(eventId)
      .then((result) => { if (!cancelled) setCms(result); })
      .catch((err: unknown) => {
        if (cancelled) return;
        reportCms('cms_resolve_failed', {
          entityKind: 'event', entityId: eventId,
          message: err instanceof Error ? err.message : 'resolve failed',
        });
        setCms({ resolved: false });
      })
      .finally(() => { if (!cancelled) setCmsSettled(true); });

    return () => { cancelled = true; };
  }, [eventId]);

  const onProblem = useCallback((problem: RenderProblem) => {
    problems.current.push(problem);
    // Anything structural means this build cannot honour the document. A non-critical
    // block that merely threw is dropped by its own boundary and is NOT a reason to
    // abandon the page — a broken lineup should not cost somebody a ticket.
    const fatal = problem.kind !== 'threw'
      || isCriticalComponent(problem.component);
    if (!fatal) return;

    reportCms('cms_fallback_used', {
      entityKind: 'event', entityId: eventId,
      reason: problem.kind,
      component: 'component' in problem ? problem.component : undefined,
      nodeId: 'nodeId' in problem ? problem.nodeId : undefined,
      critical: problem.kind === 'threw' ? true : undefined,
    });
    // Deferred: a problem is reported DURING render, and setting state synchronously from
    // a child's render is a React warning at best and a loop at worst.
    queueMicrotask(() => setCmsUnusable(true));
  }, [eventId]);

  if (loading) return <p className="detail-status">Loading event…</p>;
  if (error || !event) {
    return (
      <div className="detail-status detail-status--error">
        <p role="alert">{error ?? 'Event not found.'}</p>
        <Link to="/events" className="button button--secondary">
          Browse events
        </Link>
      </div>
    );
  }

  // Wait for the resolve to settle before choosing a path, so the page does not render
  // the legacy layout and then swap to the CMS one a moment later.
  if (!cmsSettled) return <p className="detail-status">Loading event…</p>;

  // A DOCUMENT WITHOUT ITS CRITICAL BLOCK IS NOT A USABLE DOCUMENT.
  //
  // The server refuses to publish an invalid document, so this should be unreachable —
  // but "should be" assumes the server that validated it is the server that served it,
  // and a template that simply forgot the ticket panel is valid JSON. Rendering it would
  // produce a plausible-looking event page that sells nothing, which is precisely the
  // failure the critical-block rule exists to prevent.
  //
  // The resolver-failure suite found this: a document naming a real block with a bad prop
  // value rendered an empty page, and nothing noticed.
  const hasCriticalBlock = cms?.resolved === true
    && documentUsesCriticalBlock(cms.document);

  if (cms?.resolved === true && !hasCriticalBlock && !cmsUnusable) {
    reportCms('cms_document_invalid_at_runtime', {
      entityKind: 'event', entityId: eventId, reason: 'missing_critical_block',
    });
  }

  const usable = cms?.resolved === true && hasCriticalBlock && !cmsUnusable;

  if (!usable) {
    return <EmergencyEventPage event={event} opportunity={opportunity} />;
  }

  return (
    // The theme and the referenced sections both arrive in the SAME resolve response the
    // document came from, so nothing below fetches anything. A missing theme is a normal
    // state: the provider writes no variables and every block falls back to the token
    // the application already ships.
    <CmsThemeProvider theme={cms.theme ?? null}>
      <GlobalSectionProvider sections={cms.globalSections}>
        <CmsContextProvider value={{ kind: 'event', event, opportunity }}>
          <DocumentRenderer
            document={cms.document}
            context="event"
            onProblem={onProblem}
          />
        </CmsContextProvider>
      </GlobalSectionProvider>
    </CmsThemeProvider>
  );
}

export { documentUsesCriticalBlock };
